// src/modules/DropdownFilter.js

/*
 * DropdownFilter.js
 * - ドロップダウンフィルター（西暦）でリストを絞り込む。
 * - 西暦をタップ・クリックで西暦リストを表示し、選択して、リストが絞り込む。
 * - 西暦を選択したら、ドロップダウンリストを閉じる。
 * - ドロップダウンリストの外側をクリックしたら、ドロップダウンリストを閉じる。
 * - リストには付与（`data-year`）された西暦を取得し、リストを絞り込む。
 * - 初期は最新の西暦を表示する。
 * - いずれの要素はアニメーションで表示（display: block）/非表示（display: none）する。
 * - 2024年〜2021年のリストを表示する。
 * - .js-dropdown-buttonは選択中の西暦を表示する。
 * - 西暦を選択中は、.js-dropdown-filterにクラスを付与する。
 */

import gsap from "gsap";

export default function DropdownFilter() {
  const dropdown = document.querySelector(".js-dropdown-filter");
  const dropdownButton = document.querySelector(".js-dropdown-button");
  const dropdownList = document.querySelector(".js-dropdown-list");
  const dropdownItems = document.querySelectorAll(".js-dropdown-item");
  const yearListItems = document.querySelectorAll(".js-list-item");

  // 初期表示の設定
  const initialYear = dropdownButton.textContent.trim();
  dropdownItems.forEach((item) => {
    if (item.getAttribute("data-year") === initialYear) {
      item.classList.add("is-selected");
    }
  });

  // 初期表示のリストアイテムを表示
  yearListItems.forEach((item) => {
    if (item.getAttribute("data-year") === initialYear) {
      gsap.set(item, { display: "block", opacity: 1 });
    }
  });

  // ドロップダウンボタンをクリックしたら、ドロップダウンリストを表示する。
  dropdownButton.addEventListener("click", () => {
    if (dropdownList.classList.contains("is-active")) {
      gsap.to(dropdownList, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          dropdownList.style.display = "none";
          dropdownList.classList.remove("is-active");
        },
      });
      dropdown.classList.remove("is-open"); // クラスを即時削除
    } else {
      dropdownList.style.display = "block";
      gsap.fromTo(
        dropdownList,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.5,
          ease: "power2.inOut",
          onComplete: () => {
            dropdownList.classList.add("is-active");
          },
        },
      );
      dropdown.classList.add("is-open"); // クラスを即時追加
    }
  });

  // ドロップダウンリストの外側をクリックしたら、ドロップダウンリストを閉じる。
  document.addEventListener("click", (event) => {
    if (!dropdown.contains(event.target)) {
      gsap.to(dropdownList, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          dropdownList.style.display = "none";
          dropdownList.classList.remove("is-active");
        },
      });
      dropdown.classList.remove("is-open"); // クラスを即時削除
    }
  });

  // ドロップダウンアイテムをクリックしたら、西暦を取得し、リストを絞り込む。
  dropdownItems.forEach((item) => {
    item.addEventListener("click", () => {
      const year = item.getAttribute("data-year");

      // すべてのアイテムからis-selectedクラスを削除
      dropdownItems.forEach((dropdownItem) => {
        dropdownItem.classList.remove("is-selected");
      });

      // 選択されたアイテムにis-selectedクラスを追加
      item.classList.add("is-selected");

      // ドロップダウンボタンに選択された西暦を表示
      dropdownButton.textContent = year;

      // リストアイテムの表示/非表示をアニメーションで制御
      yearListItems.forEach((listItem) => {
        const listYear = listItem.getAttribute("data-year");
        if (year === listYear || year === "全て") {
          gsap.to(listItem, { display: "block", opacity: 1, duration: 0.5, ease: "power2.inOut" });
        } else {
          gsap.to(listItem, {
            opacity: 0,
            duration: 0.5,
            ease: "power2.inOut",
            onComplete: () => {
              listItem.style.display = "none";
            },
          });
        }
      });

      // ドロップダウンリストを閉じる
      gsap.to(dropdownList, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          dropdownList.style.display = "none";
          dropdownList.classList.remove("is-active");
        },
      });
      dropdown.classList.remove("is-open"); // クラスを即時削除
    });
  });
}

// usage
// // DropdownFilter.js
// import DropdownFilter from "./modules/DropdownFilter";
// DropdownFilter();

// html
// <div class="p-dropdown-filter js-dropdown-filter">
// <button class="p-dropdown-button js-dropdown-button">2024年</button>
// <ul class="p-dropdown-list js-dropdown-list">
//     <li class="p-dropdown-item js-dropdown-item" data-year="全て">全て</li>
//     <li class="p-dropdown-item js-dropdown-item" data-year="2024年">2024年</li>
//     <li class="p-dropdown-item js-dropdown-item" data-year="2023年">2023年</li>
//     <li class="p-dropdown-item js-dropdown-item" data-year="2022年">2022年</li>
//     <li class="p-dropdown-item js-dropdown-item" data-year="2021年">2021年</li>
// </ul>
// </div>
// <div class="p-year-list">
// <div class="p-year-list__item js-list-item" data-year="2024年">2024年のリスト</div>
// <div class="p-year-list__item js-list-item" data-year="2023年">2023年のリスト</div>
// <div class="p-year-list__item js-list-item" data-year="2022年">2022年のリスト</div>
// <div class="p-year-list__item js-list-item" data-year="2021年">2021年のリスト</div>
// </div>

// css
// .p-dropdown-list {
//     display: none;
// }
// .p-dropdown-list.is-active {
//     display: block;
// }
// .p-year-list__item {
//     display: none;
// }
// .p-year-list__item.is-active {
//     display: block;
// }
// .js-dropdown-item.is-selected {
//     background-color: #f0f0f0; /* 例: 背景色を変更 */
//     color: #333; /* 例: 文字色を変更 */
// }
// .js-dropdown-filter.is-open {
//     /* ドロップダウンリストが表示されているときのスタイルをここに追加 */
// }
